/**
 * Page manager screen features
 * @module services/pm-screen
 */
import { Map, List } from 'immutable'
import _get from 'lodash/get'
import { get as getConfig } from '../../config/index'
import {
  isMemberHome,
  isYogaPage,
  isTopics,
  isEventsPage,
  isAmaEventsPage,
  isHubsAlternativeHealth,
} from '../url/index'
import { getAuthIsLoggedIn } from '../auth/index'
import {
  SCREEN_TYPE_HUBS_ALTERNATIVE_HEALTH,
} from '../page-manager/entities/index'
import {
  PM_SCREEN_TYPE_MEMBER_HOME,
  PM_SCREEN_TYPE_YOGA_HOME,
  PM_SCREEN_TYPE_CENTERS,
  PM_SCREEN_TYPE_EVENTS_PLUS,
  PM_SCREEN_TYPE_AMA_EVENTS,
} from './types'

const config = getConfig()
const hubsTestActive = !!_get(config, ['features', 'hubsTestV2'])

/**
 * Query param that overrides which member home page manager screen is loaded,
 * e.g. `/?mhScreenType=member-home-v4`.
 */
export const MH_SCREEN_TYPE_QUERY_PARAM = 'mhScreenType'

/**
 * Member home screen types follow `member-home-v<version>`, so a new page
 * manager version can be requested without adding a screen type constant.
 * Matching the convention (rather than an allowlist) keeps the override from
 * pointing page manager at unrelated screens.
 */
const MEMBER_HOME_SCREEN_TYPE_REGEX = /^member-home-v[1-9]\d*$/

/**
 * Get the member home page manager screen type for a request. A requested
 * member home version is honoured, anything else loads the default v2 screen.
 * @param {Object} options The options
 * @param {String} [options.requestedScreenType] The requested screen type override
 * @returns {String} The member home page manager screen type
 */
export function getMemberHomeVersion (options = {}) {
  const { requestedScreenType } = options
  if (MEMBER_HOME_SCREEN_TYPE_REGEX.test(requestedScreenType)) {
    return requestedScreenType
  }
  return PM_SCREEN_TYPE_MEMBER_HOME
}

/**
 * Get the page manager screen type by looking at the resolver state
 * @param {Object} options The options
 * @param {Map} options.auth The auth state
 * @param {Map} options.resolver The resolver state
 * @returns {String} The page manager screen type if the url exist
 */
export function getPmScreenTypeFromResolver (options = {}) {
  const { auth = Map(), resolver = Map() } = options
  const path = resolver.get('path')
  if (isMemberHome(path, getAuthIsLoggedIn(auth))) {
    // The resolver reducer stores the query as `fromJS(query)`, so it is a Map
    // once a location has been set and undefined before that.
    const query = resolver.get('query')
    return getMemberHomeVersion({
      requestedScreenType: Map.isMap(query)
        ? query.get(MH_SCREEN_TYPE_QUERY_PARAM)
        : undefined,
    })
  } if (isYogaPage(path)) {
    return PM_SCREEN_TYPE_YOGA_HOME
  } if (isTopics(path)) {
    return PM_SCREEN_TYPE_CENTERS
  } if (isEventsPage(path)) {
    return PM_SCREEN_TYPE_EVENTS_PLUS
  } if (hubsTestActive && isHubsAlternativeHealth(path)) {
    return SCREEN_TYPE_HUBS_ALTERNATIVE_HEALTH
  } if (isAmaEventsPage(path)) {
    return PM_SCREEN_TYPE_AMA_EVENTS
  }
  return undefined
}

/**
 * Get the page manager screen data from state
 * @param {Object} options The options
 * @param {Map} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Map} The page manager screen data
 */
export function getPmScreenData (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'data'])
}

/**
 * Get the page manager data created
 * @param {Object} options The options
 * @param {Object} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Date|undefined} The page manager screen data created date
 */
export function getPmScreenDataCreated (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'dataCreated'])
}

/**
 * Get the page manager screen sections ids from state
 * @param {Object} options The options
 * @param {Map} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Map|undefined} The page manager screen data
 */
export function getPmScreenSectionSectionIds (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  const sectionIds = pmScreen.getIn([screenType, language, 'data', 'sectionIds'], List())
  return sectionIds
}

/**
 * Get the page manager screen processing value
 * @param {Object} options The options
 * @param {Object} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Boolean|undefined} The page manager screen processing
 */
export function getPmScreenProcessing (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'processing'])
}

/**
 * Get the page manager screen error
 * @param {Object} options The options
 * @param {Object} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Error|undefined} The page manager screen error
 */
export function getPmScreenError (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'error'])
}

/**
 * Get the page manager screen error created
 * @param {Object} options The options
 * @param {Object} options.pmScreen The page manager screen state
 * @param {String} options.screenType The page manager screen type
 * @param {String} options.language The requested language
 * @returns {Date|undefined} The page manager screen error created
 */
export function getPmScreenErrorCreated (options = {}) {
  const { pmScreen = Map(), screenType, language } = options
  return pmScreen.getIn([screenType, language, 'errorCreated'])
}

export const GET_PM_SCREEN_DATE_CACHE_TIME = (60 * 1000)
export const GET_PM_SCREEN_ERROR_CACHE_TIME = (300 * 1000)

/**
 * Whether member-home navigation will (or is) refetching pmScreen, which preloads
 * the first sections including spotlight — same logic as getPmScreen in watchers-helpers.
 *
 * @param {Object} options
 * @param {Map} options.pmScreen
 * @param {string} options.screenType
 * @param {string} options.language
 * @param {Date} [options.now]
 * @returns {boolean}
 */
export function shouldPmScreenRefetch (options = {}) {
  const {
    pmScreen = Map(),
    screenType,
    language,
    now = new Date(),
  } = options

  if (!screenType) {
    return false
  }

  // SSR-seeded Member Home skips the remount GET — do not tell Spotlight a refetch is coming.
  if (pmScreen.getIn([screenType, language, 'ssrHydrated'])) {
    return false
  }

  const processing = getPmScreenProcessing({ pmScreen, screenType, language })
  if (processing) {
    return true
  }

  const errorCreated = getPmScreenErrorCreated({ pmScreen, screenType, language })
  if (errorCreated && now.getTime() < (errorCreated.getTime() + GET_PM_SCREEN_ERROR_CACHE_TIME)) {
    return false
  }

  const dataCreated = getPmScreenDataCreated({ pmScreen, screenType, language })
  if (dataCreated && now.getTime() < (dataCreated.getTime() + GET_PM_SCREEN_DATE_CACHE_TIME)) {
    return false
  }

  return true
}
